import { useFeatureDetectionContext } from "@/components/common/feature-detection-context";
import { selectZoomLensActive } from "@/store/measurement-tool-selector";
import {
  PanoMeasurementToolState,
  setPanoMeasurementToolState,
} from "@/store/measurement-tool-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { selectActiveTool } from "@/store/ui/ui-selectors";
import { ToolName } from "@/store/ui/ui-slice";
import { ClipSceneTool } from "@/tools/clip-scene-tool";
import { PanoMeasurementTool } from "@/tools/pano-measurement-tool";
import { PickingTools } from "@/tools/picking-tools";
import {
  PointerMovePreviewHandler,
  usePointerMovePreview,
} from "@/tools/pointer-move-preview-handler";
import { SceneFilter } from "@/types/scene-filter";
import { JSX, useCallback, useEffect, useState } from "react";
import type { Vector3 } from "three";
import { FlyWalkRenderables } from "../data-dispatch/flywalk-renderables";
import { FlyWalkSceneActiveElement } from "../flywalk-types";
import { useToolsAcquaintance } from "./tools-acquaintance-context";
import { useFlyWalkModeToolVisibility } from "./use-fly-walk-tools-visibility";

type FlyWalkToolsProps = {
  /** Main element in flywalk mode */
  main: FlyWalkSceneActiveElement | null;
  /** Current scene filter */
  sceneFilter: SceneFilter;
  /** Current scene renderables */
  renderables: FlyWalkRenderables;
};

/** @returns A component implementing tools for the flywalk mode */
export function FlyWalkTools({
  main,
  sceneFilter,
  renderables,
}: FlyWalkToolsProps): JSX.Element {
  const activeTool = useAppSelector(selectActiveTool);
  usePointerMovePreview(activeTool);

  const showPointerMovePreview = useFeatureDetectionContext().normalDetection;

  const dispatch = useAppDispatch();

  useEffect(() => {
    // reset pano measurement tool when switching to another tool
    if (activeTool !== ToolName.measurement) {
      dispatch(setPanoMeasurementToolState(PanoMeasurementToolState.None));
    }
  }, [activeTool, dispatch]);

  useEffect(() => {
    // reset pano measurement tool when switching to another pano
    dispatch(setPanoMeasurementToolState(PanoMeasurementToolState.None));
  }, [dispatch, renderables.pano]);

  const [enableControls, setEnableControls] = useState(true);

  const {
    tools,
    pointerMovePreview,
    setClippingBoxChanging,
    setClippingPlanes,
    setActiveToolAndPicking,
    pickingCurrentPoint,
    onValidClickInZoomLens,
    hasPointCloudBehind,
  } = useToolsAcquaintance();

  useFlyWalkModeToolVisibility(
    main ?? undefined,
    sceneFilter,
    hasPointCloudBehind,
  );

  const zoomLensOn = useAppSelector(selectZoomLensActive);

  const onPickingCurrentPointChanged = useCallback(
    (point: Vector3 | undefined) => {
      if (point) {
        pickingCurrentPoint.copy(point);
      } else {
        pickingCurrentPoint.set(NaN, NaN, NaN);
      }
    },
    [pickingCurrentPoint],
  );

  return (
    <>
      <PickingTools
        activeModels={renderables.clickableByTools}
        enableControls={enableControls}
        onActiveToolChanged={setActiveToolAndPicking}
        currentPointChanged={onPickingCurrentPointChanged}
        onValidClickInZoomLens={onValidClickInZoomLens}
        ref={tools}
      />
      <ClipSceneTool
        active={activeTool === ToolName.clipScene}
        areaBox={renderables.areaBox}
        modelBox={renderables.allModelsBox}
        clippingBoxChanging={setClippingBoxChanging}
        clippingPlanesChanged={setClippingPlanes}
      />
      {showPointerMovePreview && (
        <PointerMovePreviewHandler
          pointcloud={renderables.pointCloud ?? undefined}
          ref={pointerMovePreview}
        />
      )}
      {sceneFilter === SceneFilter.Pano &&
        activeTool === ToolName.measurement &&
        !zoomLensOn && (
          <PanoMeasurementTool
            pano={renderables.pano ?? undefined}
            setEnableControls={setEnableControls}
          />
        )}
    </>
  );
}
